import {
  ErrorBody,
  FILE_NAME_HEADER,
  buildPath,
  type AnyEndpoint,
  type EndpointInput,
  type EndpointResponse,
  type ErrorCode,
} from '@itc/shared';

// The typed API client (design §7.3, §8). `api(contract, input)` checks its input
// type against the contract, sends the CSRF header on changes, parses the response
// with the contract's schema, and turns the shared error shape into ApiError.

export class ApiError extends Error {
  override name = 'ApiError';
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: unknown;

  constructor(status: number, code: ErrorCode, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiCallOptions {
  signal?: AbortSignal;
  /** Send the browser to /login on 401. Default true; the user store turns it off. */
  redirectOnUnauthorized?: boolean;
  /**
   * Sent as Idempotency-Key, for contracts that accept one (design §6.12): a retry with
   * the same key replays the first response instead of repeating the change.
   */
  idempotencyKey?: string;
}

/** Endpoints without params, query or body can be called with no input. */
export type ApiArgs<E extends AnyEndpoint> = object extends EndpointInput<E>
  ? [input?: EndpointInput<E>, options?: ApiCallOptions]
  : [input: EndpointInput<E>, options?: ApiCallOptions];

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const CSRF_COOKIES = ['__Host-XSRF-TOKEN', 'XSRF-TOKEN'];

function readCookie(names: readonly string[]): string | undefined {
  const cookies = (globalThis.document as Document | undefined)?.cookie ?? '';
  for (const name of names) {
    for (const part of cookies.split(';')) {
      const [key, ...value] = part.trim().split('=');
      if (key === name) return decodeURIComponent(value.join('='));
    }
  }
  return undefined;
}

function queryString(query: unknown): string {
  if (query === undefined || query === null) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    const values: unknown[] = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === undefined || item === null) continue;
      if (typeof item !== 'string' && typeof item !== 'number' && typeof item !== 'boolean') {
        throw new Error(`Query parameter "${key}" must be a string, number or boolean`);
      }
      params.append(key, String(item));
    }
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/** Where the browser goes when the API says the caller needs to log in. Replaceable for tests. */
export const navigation = {
  toLogin(next: string): void {
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  },
  /** A sensitive action needs a fresh Discord login (design D24). */
  toReauthenticate(next: string): void {
    window.location.assign(`/api/auth/login?next=${encodeURIComponent(next)}`);
  },
  current(): string {
    return window.location.pathname + window.location.search;
  },
};

/** Where a stored file is read, for an `<img src>`. Its module decides who gets it. */
export const fileUrl = (id: string): string => `/api/files/${encodeURIComponent(id)}`;

export async function api<E extends AnyEndpoint>(endpoint: E, ...args: ApiArgs<E>): Promise<EndpointResponse<E>> {
  const [input, options = {}] = args as [(Partial<Record<'params' | 'query' | 'body', unknown>> & { file?: Blob }) | undefined, ApiCallOptions?];
  const url = `/api${buildPath(endpoint.path, input?.params as Record<string, unknown> | undefined)}${queryString(input?.query)}`;

  const headers: Record<string, string> = { accept: 'application/json' };
  if (!SAFE_METHODS.has(endpoint.method)) {
    const token = readCookie(CSRF_COOKIES);
    if (token !== undefined) headers['x-csrf-token'] = token;
  }
  if (options.idempotencyKey !== undefined) {
    if (endpoint.idempotencyKey === 'none') throw new Error(`${endpoint.method} ${endpoint.path} doesn't take an Idempotency-Key`);
    headers['idempotency-key'] = options.idempotencyKey;
  }
  let body: string | Blob | undefined;
  if (input?.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(input.body);
  }
  if (input?.file !== undefined) {
    // The server goes by the bytes; a type is sent only so that one is.
    headers['content-type'] = input.file.type === '' ? 'application/octet-stream' : input.file.type;
    if (input.file instanceof File) headers[FILE_NAME_HEADER] = encodeURIComponent(input.file.name);
    body = input.file;
  }

  const response = await fetch(url, {
    method: endpoint.method,
    headers,
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body }),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  const payload: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const parsed = ErrorBody.safeParse(payload);
    const error = parsed.success
      ? new ApiError(response.status, parsed.data.error.code, parsed.data.error.message, parsed.data.error.details)
      : new ApiError(response.status, response.status >= 500 ? 'internal' : 'bad_request', `Request failed with ${String(response.status)}`);
    if (options.redirectOnUnauthorized !== false) {
      if (error.code === 'recent_login_required') navigation.toReauthenticate(navigation.current());
      // A request still in flight when the user logged out lands here once they're on /login: they're already there.
      else if (error.status === 401 && !/^\/login(\?|$)/.test(navigation.current())) navigation.toLogin(navigation.current());
    }
    throw error;
  }

  return (await endpoint.response.parseAsync(payload)) as EndpointResponse<E>;
}
